import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPlus, FaTimes, FaTrash, FaEdit, FaBoxes, FaChevronDown, FaChevronUp, FaExclamationTriangle, FaFileAlt, FaSync } from 'react-icons/fa';
import { FiCheckCircle } from 'react-icons/fi';
import InputProductoFacturacion from '../util/Inputs/InputProductoFacturacion';
import TablaEntidad from '../util/tablas/TablaEntidad';
import PlantillaModal from '../util/PlantillaModal';
import DockVerticalDerecha from '../estructura/DockVerticalDerecha';
import FuncionesGenerales from '../util/FuncionesGenerales';
import { useContextoSesion } from '../../miscelaneo/ContextoSesion';
import SpinnerCargando from '../util/SpinnerCargando';
import ModalPagoPOS from '../venta/ModalPagoPOS';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import ModalPDFBase64 from '../util/ModalPDFBase64';
import useCompraTicketManager from '../../hooks/useCompraTicketManager';
import { roundTo2 } from '../../miscelaneo/monetario';
import { obtenerIdMetodoPago } from '../../miscelaneo/facturacionHelpers';

function crearClaveIdempotenciaFiscal(prefijo = 'fiscal') {
  if (window?.crypto?.randomUUID) {
    return `${prefijo}-${window.crypto.randomUUID()}`;
  }
  return `${prefijo}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * ProcesoCompraUnificado
 * 
 * Componente de compras con UX unificada al estilo POS.
 * - Multi-ticket simultáneo
 * - Búsqueda rápida de productos por código de barras / nombre
 * - Selección de proveedor
 * - Pagos integrados (contado / crédito / abono)
 * - Soporte para NCF informal (E41/B11)
 * - Contingencia Serie B
 */
export default function ProcesoCompraUnificado() {
  const location = useLocation();
  const { usuario, sesionCaja, sesionCajaCargada, cargando: cargandoContexto } = useContextoSesion();
  const { formatoMoneda, obtenerFechaHoraActual } = FuncionesGenerales(usuario);

  // Hook compartido para gestión de tickets de compra
  const tm = useCompraTicketManager();

  // Estados de contingencia Serie B (para compras informales E41)
  const [mostrarConfirmacionSerieB, setMostrarConfirmacionSerieB] = useState(false);
  const [payloadPendiente, setPayloadPendiente] = useState(null);
  const [mensajeContingencia, setMensajeContingencia] = useState('');
  const [idCompraReintentando, setIdCompraReintentando] = useState(null);

  // Estados para edición de compras
  const [resumenInventario, setResumenInventario] = useState(null);
  const [mostrarResumenInventario, setMostrarResumenInventario] = useState(false);
  const [pagosAnulados, setPagosAnulados] = useState(0);
  const [confirmacionPagosAnulados, setConfirmacionPagosAnulados] = useState(false);

  // Helper: determinar si el ticket activo está en modo edición
  const enModoEdicion = !!tm.ticketActual?.id_compra_editando;

  // ================================================================
  // Cargar compra existente para editar
  // ================================================================
  const cargarCompraParaEditar = async (compra) => {
    try {
      const response = await instanciaAxios.get(`/compra/${compra.id_compra}`);
      const compraCompleta = response.data;

      // detalles_compra es JSONB array con: id_producto, cantidad, costo, descuento, indicador_facturacion, itbis_tasa, descripcion
      const detallesRaw = compraCompleta.detalles_compra || [];
      const productos = detallesRaw.map(det => ({
        id_producto: det.id_producto,
        descripcion: det.descripcion || '',
        precio: parseFloat(det.costo) || 0,
        cantidad: parseFloat(det.cantidad) || 1,
        indicador_facturacion: det.indicador_facturacion || 1,
        itbis_tasa: parseFloat(det.itbis_tasa) || 0,
        id_tipo_producto: 1
      }));

      const productosOriginales = productos.map(p => ({ ...p }));

      const nuevosTickets = [...tm.tickets];
      const ticket = nuevosTickets[tm.ticketActivo];

      ticket.productos = productos;
      ticket.proveedorSeleccionado = compraCompleta.id_entidad_proveedor || null;
      ticket.nombreProveedor = compraCompleta.nombre_proveedor || '';
      ticket.comentario = compraCompleta.comentario || '';
      ticket.es_informal = compraCompleta.es_informal || false;
      ticket.ncf = compraCompleta.ncf || '';
      ticket.id_compra_editando = compra.id_compra;
      ticket.productosOriginales = productosOriginales;

      tm.calcularTotales(ticket);
      tm.setTickets(nuevosTickets);

      // Seleccionar la sucursal de la compra si no está seleccionada
      if (compraCompleta.id_sucursal && !tm.sucursalSeleccionada) {
        tm.setSucursalSeleccionada(String(compraCompleta.id_sucursal));
      }

      tm.setMostrarModalBusquedaCompras(false);
      toast.success(`Compra #${compra.id_compra} cargada para edición`);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar compra:', error);
      toast.error('Error al cargar la compra para edición');
    }
  };

  // ================================================================
  // Actualizar compra existente (modo edición)
  // ================================================================
  const actualizarCompraExistente = async (ticket) => {
    const detallesCompra = ticket.productos.map(p => ({
      id_producto: p.id_producto,
      descripcion: p.descripcion,
      cantidad: p.cantidad,
      precio: p.precio,
      descuento: 0,
      indicador_facturacion: p.indicador_facturacion,
      itbis_tasa: p.itbis_tasa
    }));

    const payload = {
      id_entidad_proveedor: ticket.proveedorSeleccionado,
      id_sucursal: tm.sucursalSeleccionada,
      comentario: ticket.comentario || `Compra editada - ${ticket.nombre}`,
      detallesCompra,
      es_informal: ticket.es_informal,
      monto: roundTo2(ticket.totales.subtotal),
      impuesto_total: roundTo2(ticket.totales.itbis),
      monto_total: roundTo2(ticket.totales.total)
    };

    const respuesta = await instanciaAxios.put(`/compra/${ticket.id_compra_editando}/completa`, payload);
    const data = respuesta.data?.data || respuesta.data || {};

    console.log('[EDIT RESPONSE compra]', { pagos_anulados: data.pagos_anulados, resumen_inventario: data.resumen_inventario });

    // ALWAYS show modal after edit — sections render conditionally inside
    setResumenInventario(data.resumen_inventario || []);
    setPagosAnulados(data.pagos_anulados || 0);
    setConfirmacionPagosAnulados(data.pagos_anulados > 0 ? false : true);
    setMostrarResumenInventario(true);
    // Don't clean ticket yet - wait for user to click "Entendido"
    return;
  };

  // ================================================================
  // Construir payload de compra
  // ================================================================
  const construirPayloadCompra = (ticket, datosPago) => {
    const fechaHoy = obtenerFechaHoraActual().split('T')[0];
    const condicionPago = datosPago.tipoPago;

    const detallesCompra = ticket.productos.map(p => ({
      id_producto: p.id_producto,
      descripcion: p.descripcion,
      cantidad: p.cantidad,
      precio: p.precio,
      descuento: 0,
      indicador_facturacion: p.indicador_facturacion,
      itbis_tasa: p.itbis_tasa
    }));

    const incluirPagos = condicionPago === 'contado' || datosPago.abonoInicial !== null;

    const pagosProgramados = incluirPagos ? datosPago.metodosPago.map(p => ({
      monto: roundTo2(parseFloat(p.monto) || 0),
      id_metodo_pago: obtenerIdMetodoPago(p.metodoPago),
      fecha_pago: fechaHoy,
      referencia: p.detalles?.referencia || null,
      id_cuenta_tesoreria: p.detalles?.cuentaDestino ? parseInt(p.detalles.cuentaDestino) : null,
      id_pasarela_pago: p.detalles?.id_pasarela_pago || null,
      comentario: p.detalles?.comentario || null
    })) : [];

    let fechaVencimiento = fechaHoy;
    if (condicionPago === 'credito') {
      const fecha = new Date();
      fecha.setDate(fecha.getDate() + 30);
      fechaVencimiento = fecha.toISOString().split('T')[0];
    }

    return {
      fecha_hora: obtenerFechaHoraActual(),
      numero_compra: '',
      fecha_vencimiento: fechaVencimiento,
      monto: roundTo2(ticket.totales.subtotal),
      impuesto_total: roundTo2(ticket.totales.itbis),
      monto_total: roundTo2(ticket.totales.total),
      descuento_total: 0,
      comentario: ticket.comentario || `Compra - ${ticket.nombre}`,
      ncf: ticket.ncf || '',
      es_informal: ticket.es_informal,
      id_sucursal: tm.sucursalSeleccionada,
      id_centro_costo: ticket.id_centro_costo || null,
      id_entidad_proveedor: ticket.proveedorSeleccionado,
      detallesCompra,
      incluirPagos,
      pagosProgramados
    };
  };

  // ================================================================
  // Validar y abrir modal de pago
  // ================================================================
  const cobrarCompra = () => {
    const ticket = tm.ticketActual;

    if (ticket.productos.length === 0) {
      toast.error('La compra está vacía');
      return;
    }

    if (!tm.sucursalSeleccionada) {
      toast.error('Debe seleccionar una sucursal');
      return;
    }

    if (!ticket.proveedorSeleccionado) {
      toast.error('Debe seleccionar un proveedor');
      return;
    }

    // En modo edición, ejecutar directamente sin modal de pago
    if (ticket.id_compra_editando) {
      procesarCompra(null);
      return;
    }

    tm.setMostrarModalPago(true);
  };

  // ================================================================
  // Procesar pago / registrar compra
  // ================================================================
  const procesarCompra = async (datosPago) => {
    const ticket = tm.ticketActual;
    if (tm.procesandoCobro) return;

    try {
      tm.setProcesandoCobro(true);

      if (ticket.id_compra_editando) {
        // === MODO EDICIÓN: Actualizar compra existente ===
        await actualizarCompraExistente(ticket);
      } else {
        // === MODO CREACIÓN: Nueva compra ===
        const claveIdempotencia = crearClaveIdempotenciaFiscal('compra');
        const payload = {
          ...construirPayloadCompra(ticket, datosPago),
          clave_idempotencia: claveIdempotencia
        };
        console.log('📦 Payload de compra:', payload);

        const respuesta = await instanciaAxios.post('/compra/', payload, {
          headers: { 'x-idempotency-key': claveIdempotencia }
        });
        const data = respuesta.data?.data || respuesta.data || {};
        if (respuesta.data?.pendienteEnvio || data.dgii_estado === 'PendienteEnvio') {
          toast.warning(
            `⚠️ Compra registrada con NCF ${data.ncf || ''}. Envío DGII pendiente; use reintento cuando el servicio esté disponible.`,
            { autoClose: 9000 }
          );
        } else {
          toast.success(respuesta.data?.mensaje || 'Compra registrada correctamente');
        }

        tm.setCompraCreada(data);

        // Mostrar PDF automáticamente si la compra generó NCF (E41/B11)
        if (data.pdf?.pdf_base64) {
          tm.setPdfBase64(data.pdf.pdf_base64);
          tm.setIdCompraPDF(data.id_compra);
          tm.setMostrarPDFCompra(true);
        } else if (data.id_compra && data.es_electronica) {
          tm.setIdCompraPDF(data.id_compra);
          tm.setMostrarPDFCompra(true);
        }

        tm.limpiarTicketActivo();
        tm.setMostrarModalPago(false);
      }

    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al procesar compra:', error);

      if (error.response?.status === 400) {
        const mensaje = error.response?.data?.mensaje || '';
        const errorCode = error.response?.data?.error_code || '';

        if (errorCode === 'STOCK_INSUFICIENTE') {
          toast.error(`Stock insuficiente:\n\n${mensaje}`, { autoClose: 10000 });
          return;
        }
        if (errorCode === 'CUENTA_TESORERIA_REQUERIDA') {
          toast.error(mensaje || 'Debe tener una caja abierta o seleccionar cuenta de tesorería');
          return;
        }
        if (errorCode === 'COMPRA_ECF_INMUTABLE') {
          toast.error('Esta compra tiene comprobante electrónico E41 y no puede ser editada.');
          return;
        }
        toast.error(mensaje || 'Error de validación. Revise los datos ingresados');
        return;
      }

      // Contingencia Serie B para compras informales (E41 → B11)
      if (error.response?.status === 409 && error.response.data?.puedeUsarB) {
        const ticket = tm.ticketActual;
        const claveIdempotencia = crearClaveIdempotenciaFiscal('compra-serie-b');
        setPayloadPendiente({
          ...construirPayloadCompra(ticket, datosPago),
          clave_idempotencia: claveIdempotencia,
          usarSerieB: true
        });
        setMensajeContingencia(
          error.response.data?.mensaje || 'Serie E agotada. ¿Desea usar Serie B (contingencia)?'
        );
        setMostrarConfirmacionSerieB(true);
        tm.setMostrarModalPago(false);
        return;
      }

      const mensajeError = error.response?.data?.mensaje || error.response?.data?.message || 'Error al procesar la compra';
      toast.error(mensajeError);
    } finally {
      tm.setProcesandoCobro(false);
    }
  };

  // ================================================================
  // Confirmar uso de Serie B (contingencia)
  // ================================================================
  const confirmarUsarSerieB = async () => {
    if (!payloadPendiente) return;

    try {
      tm.setProcesandoCobro(true);
      setMostrarConfirmacionSerieB(false);

      const respuesta = await instanciaAxios.post('/compra/', payloadPendiente, {
        headers: payloadPendiente?.clave_idempotencia
          ? { 'x-idempotency-key': payloadPendiente.clave_idempotencia }
          : {}
      });
      toast.warning('⚠️ Compra creada con Serie B (contingencia)');

      const data = respuesta.data?.data || respuesta.data || {};
      tm.setCompraCreada(data);

      // Mostrar PDF automáticamente (B11 también genera PDF)
      if (data.pdf?.pdf_base64) {
        tm.setPdfBase64(data.pdf.pdf_base64);
        tm.setIdCompraPDF(data.id_compra);
        tm.setMostrarPDFCompra(true);
      } else if (data.id_compra) {
        tm.setIdCompraPDF(data.id_compra);
        tm.setMostrarPDFCompra(true);
      }

      tm.limpiarTicketActivo();
      setPayloadPendiente(null);

    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al crear compra con Serie B:', error);
      const mensaje = error.response?.data?.mensaje || '';
      toast.error(mensaje || 'Error al crear compra con Serie B');
    } finally {
      tm.setProcesandoCobro(false);
    }
  };

  // ================================================================
  // Reintentar envío DGII para compras E41 pendientes
  // ================================================================
  const reintentarEnvioCompraDGII = async (idCompra) => {
    if (!idCompra || idCompraReintentando === idCompra) return;
    try {
      setIdCompraReintentando(idCompra);
      const respuesta = await instanciaAxios.post(`/compra/${idCompra}/reenviar-dgii`);
      if (respuesta.data?.exito) {
        toast.success(respuesta.data?.mensaje || `Reintento enviado para compra #${idCompra}`);
        // Refrescar lista con estado más reciente
        await tm.buscarCompras(tm.filtrosBusqueda);
      } else {
        toast.error(respuesta.data?.mensaje || 'No se pudo reintentar el envío DGII');
      }
    } catch (error) {
      const mensaje = error.response?.data?.mensaje || 'Error al reintentar envío DGII de la compra';
      toast.error(mensaje);
    } finally {
      setIdCompraReintentando(null);
    }
  };

  // ================================================================
  // Loading state
  // ================================================================
  if (tm.cargando || cargandoContexto || !sesionCajaCargada) {
    return <SpinnerCargando />;
  }

  const ticketActual = tm.ticketActual;

  // ================================================================
  // Verificar sesión de caja
  // ================================================================
  if (!sesionCaja) {
    return (
      <div className="min-h-screen bg-gray-200 font-sans">
        <div className="flex justify-center py-20">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-10 max-w-md text-center">
            <div className="bg-yellow-100 rounded-full p-5 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <FaExclamationTriangle className="text-yellow-500 text-3xl" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-3">Caja no abierta</h2>
            <p className="text-gray-600 mb-6">
              Debes abrir una caja antes de poder registrar compras con pago integrado.
            </p>
            <Link
              to="/AperturaCaja"
              state={{ from: location.pathname }}
              className="inline-block px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-all duration-200 shadow-md"
            >
              Abrir Caja
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (sesionCaja.es_sesion_vieja) {
    return (
      <div className="min-h-screen bg-gray-200 font-sans">
        <div className="flex justify-center py-20">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-10 max-w-md text-center">
            <div className="bg-red-100 rounded-full p-5 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <FaExclamationTriangle className="text-red-500 text-3xl" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-3">Caja de otro día</h2>
            <p className="text-gray-600 mb-6">
              Tienes una caja abierta de un día anterior ({sesionCaja.nombre_caja}). Debes cerrarla antes de continuar.
            </p>
            <a
              href="/CierreCaja"
              className="inline-block px-6 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all duration-200 shadow-md"
            >
              Cerrar Caja
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ================================================================
  // Render principal
  // ================================================================
  return (
    <div className="min-h-screen bg-gray-200 font-sans">
      <div className="flex justify-center py-10">
        <div className="bg-gray-100 bg-opacity-90 rounded-xl border p-8 w-full max-w-7xl mx-10">
          
          {/* Header */}
          <div className="space-y-1 mb-6">
            <h1 className="font-cascadia font-bold text-2xl flex items-center gap-2">
              <FaBoxes className="text-color-secundario" />
              Registro de Compras
              {enModoEdicion && (
                <span className="ml-3 px-3 py-1 text-sm font-semibold bg-amber-100 text-amber-700 rounded-lg border border-amber-300">
                  ✏️ Editando Compra #{ticketActual.id_compra_editando}
                </span>
              )}
            </h1>
            <p className="text-gray-500">
              {enModoEdicion
                ? 'Modifica los productos y guarda los cambios — el inventario se ajustará automáticamente'
                : 'Registra compras con pagos integrados — múltiples tickets simultáneos'}
            </p>
          </div>

          {/* Selector/Label de Sucursal */}
          <div className="mb-6">
            {!tm.sucursalSeleccionada ? (
              <div className="bg-white p-4 rounded-xl border border-gray-300">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sucursal <span className="text-red-500">*</span>
                </label>
                <select
                  value={tm.sucursalSeleccionada}
                  onChange={(e) => tm.setSucursalSeleccionada(e.target.value)}
                  className="w-full p-2 rounded-xl text-gray-700 border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                >
                  <option value="">Seleccione una sucursal</option>
                  {tm.sucursales.map((s) => (
                    <option key={s.id_sucursal} value={s.id_sucursal}>
                      {s.descripcion}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="flex justify-start items-center">
                <div className="bg-color-primario text-white px-4 py-2 rounded-lg flex items-center gap-3">
                  <span className="font-semibold">
                    Sucursal: {tm.sucursales.find(s => s.id_sucursal === parseInt(tm.sucursalSeleccionada))?.descripcion}
                  </span>
                  {!tm.sucursalDefecto && (
                    <button
                      onClick={() => {
                        if (tm.tickets.some(t => t.productos.length > 0)) {
                          if (!window.confirm('Hay tickets con productos. ¿Seguro que desea cambiar de sucursal?')) {
                            return;
                          }
                        }
                        tm.setSucursalSeleccionada('');
                      }}
                      className="text-xs bg-white text-color-primario px-2 py-1 rounded hover:bg-gray-100 transition"
                    >
                      Cambiar
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sistema de Tabs */}
          <div className="mb-6">
            <div 
              className="flex items-center gap-2 overflow-x-auto pb-2"
              style={{ scrollbarWidth: 'thin', scrollbarColor: '#9CA3AF #E5E7EB' }}
            >
              {tm.tickets.map((ticket, index) => (
                <div
                  key={ticket.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-t-lg border-b-2 transition-colors cursor-pointer flex-shrink-0 whitespace-nowrap ${
                    tm.ticketActivo === index
                      ? 'bg-color-secundario text-white border-color-secundario'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => tm.setTicketActivo(index)}
                >
                  <span className="font-medium text-sm">{ticket.nombre}</span>
                  {ticket.productos.length > 0 && (
                    <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                      tm.ticketActivo === index 
                        ? 'bg-white text-color-secundario' 
                        : 'bg-color-secundario text-white'
                    }`}>
                      {ticket.productos.length}
                    </span>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      tm.cerrarTicket(index);
                    }}
                    className="hover:text-red-500 transition-colors"
                  >
                    <FaTimes size={12} />
                  </button>
                </div>
              ))}
              
              {tm.tickets.length < 10 && (
                <button
                  onClick={tm.crearNuevoTicket}
                  className="flex items-center gap-1 px-3 py-2 bg-color-primario text-white rounded-t-lg hover:bg-opacity-90 transition-colors flex-shrink-0 text-sm"
                >
                  <FaPlus size={10} />
                  Nuevo
                </button>
              )}
            </div>
          </div>

          {/* Información del Ticket Activo */}
          <div className="mb-6 bg-white p-4 rounded-xl border border-gray-300">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Información de {ticketActual.nombre}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Proveedor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Proveedor <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={tm.handleClickBuscarProveedor}
                  className="w-full flex items-center justify-between p-2 rounded-xl text-gray-700 border border-gray-400 bg-white hover:border-verde-oscuro transition-colors text-left"
                >
                  <span className="truncate">
                    {ticketActual.proveedorSeleccionado 
                      ? ticketActual.nombreProveedor 
                      : 'Seleccionar proveedor...'}
                  </span>
                  {ticketActual.proveedorSeleccionado && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const nuevosTickets = [...tm.tickets];
                        nuevosTickets[tm.ticketActivo].proveedorSeleccionado = null;
                        nuevosTickets[tm.ticketActivo].nombreProveedor = '';
                        tm.setTickets(nuevosTickets);
                      }}
                      className="ml-2 text-red-500 hover:text-red-700 transition-colors"
                    >
                      <FaTimes size={14} />
                    </button>
                  )}
                </button>
              </div>

              {/* NCF Informal */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comprobante fiscal
                </label>
                <div className="flex items-center gap-3 p-2 border border-gray-300 rounded-xl bg-gray-50">
                  <input
                    type="checkbox"
                    checked={ticketActual.es_informal}
                    onChange={(e) => {
                      const nuevosTickets = [...tm.tickets];
                      nuevosTickets[tm.ticketActivo].es_informal = e.target.checked;
                      tm.setTickets(nuevosTickets);
                    }}
                    className="w-4 h-4 text-color-secundario rounded"
                    id="chk_informal"
                  />
                  <label htmlFor="chk_informal" className="text-sm text-gray-700 cursor-pointer select-none">
                    Asignar NCF de compra (E41 / B11)
                  </label>
                </div>
                {ticketActual.es_informal && (
                  <p className="text-xs text-amber-600 mt-1">
                    Se generará un comprobante de compra automáticamente. Retención ITBIS 100%.
                  </p>
                )}
                {!ticketActual.es_informal && (
                  <div className="mt-2">
                    <input
                      type="text"
                      value={ticketActual.ncf || ''}
                      onChange={(e) => {
                        const nuevosTickets = [...tm.tickets];
                        nuevosTickets[tm.ticketActivo].ncf = e.target.value;
                        tm.setTickets(nuevosTickets);
                      }}
                      className="w-full p-2 rounded-xl text-gray-700 border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      placeholder="NCF del proveedor (si aplica)"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Campos adicionales */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <button
                onClick={() => tm.setMostrarCamposAdicionales(!tm.mostrarCamposAdicionales)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-color-primario transition-colors"
              >
                {tm.mostrarCamposAdicionales ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                <span>Campos adicionales</span>
              </button>

              {tm.mostrarCamposAdicionales && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 animate-fadeIn">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Comentario
                    </label>
                    <textarea
                      value={ticketActual.comentario || ''}
                      onChange={(e) => {
                        const nuevosTickets = [...tm.tickets];
                        nuevosTickets[tm.ticketActivo].comentario = e.target.value;
                        tm.setTickets(nuevosTickets);
                      }}
                      className="w-full p-2 rounded-xl text-gray-700 border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      placeholder="Agregar comentario..."
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Centro de costo
                    </label>
                    <select
                      value={ticketActual.id_centro_costo || ''}
                      onChange={(e) => {
                        const nuevosTickets = [...tm.tickets];
                        nuevosTickets[tm.ticketActivo].id_centro_costo = e.target.value ? parseInt(e.target.value) : null;
                        tm.setTickets(nuevosTickets);
                      }}
                      className="w-full p-2 rounded-xl text-gray-700 border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    >
                      <option value="">Sin centro de costo</option>
                      {tm.centrosCosto.map((centro) => (
                        <option key={centro.id_centro_costo} value={centro.id_centro_costo}>
                          {centro.descripcion}
                        </option>
                      ))}
                    </select>
                  </div>

                </div>
              )}
            </div>
          </div>

          {/* Layout principal: Búsqueda + Lista + Totales */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Columna izquierda y centro: Búsqueda y Lista */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Búsqueda de producto */}
              <div className="bg-white p-6 rounded-xl border border-gray-300">
                <label className="block text-lg font-semibold text-gray-800 mb-3">
                  Buscar Producto
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Escanea código de barras o busca por nombre/referencia
                </p>
                <InputProductoFacturacion
                  ref={tm.inputProductoRef}
                  onProductoSeleccionado={tm.handleProductoSeleccionado}
                  onProductoRapido={tm.agregarProductoRapido}
                  onClickConProducto={tm.handleClickInputConProducto}
                  id_sucursal={tm.sucursalSeleccionada}
                  modoFacturacion="compra"
                  editable={!!tm.sucursalSeleccionada}
                  placeholder="Referencia, código de barras o ID"
                />
              </div>

              {/* Lista de productos */}
              <div className="bg-white p-6 rounded-xl border border-gray-300">
                <h2 className="text-lg font-semibold mb-4">
                  Productos en {ticketActual.nombre}
                </h2>
                
                {ticketActual.productos.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <FaBoxes size={48} className="mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Compra vacía</p>
                    <p className="text-sm">Agrega productos para comenzar</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-color-primario text-white">
                        <tr>
                          <th className="px-4 py-3 text-left">Producto</th>
                          <th className="px-4 py-3 text-right">Costo</th>
                          {enModoEdicion && <th className="px-4 py-3 text-center">Ant.</th>}
                          <th className="px-4 py-3 text-center">Cant.</th>
                          <th className="px-4 py-3 text-right">Subtotal</th>
                          <th className="px-4 py-3 text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ticketActual.productos.map((producto, index) => {
                          const cantOriginal = enModoEdicion
                            ? (ticketActual.productosOriginales || []).find(o => o.id_producto === producto.id_producto)?.cantidad
                            : null;
                          const esNuevoProducto = enModoEdicion && cantOriginal === undefined;
                          const cantidadCambio = enModoEdicion && cantOriginal !== undefined ? producto.cantidad - cantOriginal : null;

                          return (
                            <tr
                              key={index}
                              className={`border-b hover:bg-gray-50 cursor-pointer ${esNuevoProducto ? 'bg-green-50' : ''}`}
                              onClick={() => tm.editarCantidadProducto(index)}
                            >
                              <td className="px-4 py-3">
                                <div className="font-medium flex items-center gap-2">
                                  {producto.descripcion}
                                  {esNuevoProducto && (
                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Nuevo</span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {producto.indicador_facturacion === 4 ? 'Exento' : `ITBIS ${producto.itbis_tasa}%`}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span>{formatoMoneda(producto.precio)}</span>
                              </td>
                              {enModoEdicion && (
                                <td className="px-4 py-3 text-center text-gray-400 text-sm">
                                  {cantOriginal !== undefined ? cantOriginal : '—'}
                                </td>
                              )}
                              <td className="px-4 py-3 text-center font-semibold">
                                {producto.cantidad}
                                {cantidadCambio !== null && cantidadCambio !== 0 && (
                                  <span className={`ml-1 text-xs ${cantidadCambio > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    ({cantidadCambio > 0 ? '+' : ''}{cantidadCambio})
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right font-semibold text-color-secundario">
                                {formatoMoneda(producto.precio * producto.cantidad + tm.calcularImpuestoPorProducto(producto))}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex justify-center gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      tm.editarCantidadProducto(index);
                                    }}
                                    className="text-color-primario hover:text-color-secundario transition"
                                    title="Editar cantidad"
                                  >
                                    <FaEdit size={16} />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      tm.eliminarProducto(index);
                                    }}
                                    className="text-red-500 hover:text-red-700 transition"
                                    title="Eliminar"
                                  >
                                    <FaTrash size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Columna derecha: Panel de totales */}
            <div className="lg:col-span-1">
              <div className="bg-white p-6 rounded-xl border border-gray-300 sticky top-4">
                <h2 className="text-lg font-semibold mb-4 text-gray-800">
                  Resumen de Compra
                </h2>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal:</span>
                    <span className="font-medium">{formatoMoneda(ticketActual.totales.subtotal)}</span>
                  </div>
                  
                  <div className="flex justify-between text-gray-600">
                    <span>ITBIS:</span>
                    <span className="font-medium">{formatoMoneda(ticketActual.totales.itbis)}</span>
                  </div>
                  
                  {ticketActual.es_informal && (
                    <div className="flex justify-between text-amber-600">
                      <span>Retención ITBIS:</span>
                      <span className="font-medium">-{formatoMoneda(ticketActual.totales.itbis)}</span>
                    </div>
                  )}
                  
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold text-gray-800">TOTAL:</span>
                      <span className="text-2xl font-bold text-green-600">
                        {formatoMoneda(ticketActual.totales.total)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Proveedor seleccionado badge */}
                {ticketActual.proveedorSeleccionado && (
                  <div className="mb-4 p-2 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-xs text-blue-600 font-medium">Proveedor:</p>
                    <p className="text-sm font-semibold text-blue-800 truncate">
                      {ticketActual.nombreProveedor}
                    </p>
                  </div>
                )}

                {/* Botón Registrar Compra */}
                <button
                  onClick={cobrarCompra}
                  disabled={ticketActual.productos.length === 0 || !tm.sucursalSeleccionada || !ticketActual.proveedorSeleccionado || tm.procesandoCobro}
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                    ticketActual.productos.length === 0 || !tm.sucursalSeleccionada || !ticketActual.proveedorSeleccionado || tm.procesandoCobro
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl'
                  }`}
                >
                  {tm.procesandoCobro ? 'PROCESANDO...' : (enModoEdicion ? 'ACTUALIZAR COMPRA' : 'REGISTRAR COMPRA')}
                </button>
                
                {ticketActual.productos.length === 0 && (
                  <p className="text-xs text-gray-400 text-center mt-2">
                    Agrega productos para registrar
                  </p>
                )}
                {!tm.sucursalSeleccionada && ticketActual.productos.length > 0 && (
                  <p className="text-xs text-red-500 text-center mt-2">
                    Selecciona una sucursal
                  </p>
                )}
                {!ticketActual.proveedorSeleccionado && ticketActual.productos.length > 0 && tm.sucursalSeleccionada && (
                  <p className="text-xs text-red-500 text-center mt-2">
                    Selecciona un proveedor
                  </p>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Modal para especificar cantidad */}
      <PlantillaModal
        mostrar={tm.mostrarModalCantidad}
        onClose={() => {
          tm.setMostrarModalCantidad(false);
          tm.setProductoTemporal(null);
          tm.setCantidadTemporal('1');
          tm.setPrecioTemporal('');
          tm.setModoModal('agregar');
          tm.setIndexProductoEditar(null);
          tm.setModoPrecioTemporal('unitario');
          setTimeout(() => {
            tm.inputProductoRef.current?.limpiar?.();
          }, 100);
        }}
        titulo={tm.modoModal === 'editar' ? 'Editar Producto' : 'Agregar Producto'}
      >
        {tm.productoTemporal && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">Producto:</p>
              <p className="font-semibold text-lg">{tm.productoTemporal.descripcion}</p>
              {tm.productoTemporal.descripcion_unidad_medida && (
                <p className="text-sm text-gray-500 italic mt-1">
                  Unidad: {tm.productoTemporal.descripcion_unidad_medida}
                </p>
              )}
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Costo unitario:</label>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs">
                  <button
                    type="button"
                    onClick={() => tm.setModoPrecioTemporal('unitario')}
                    className={`px-2 py-1 transition-colors ${tm.modoPrecioTemporal === 'unitario' ? 'bg-color-primario text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    Sin impuestos
                  </button>
                  <button
                    type="button"
                    onClick={() => tm.setModoPrecioTemporal('total')}
                    className={`px-2 py-1 transition-colors border-l border-gray-300 ${tm.modoPrecioTemporal === 'total' ? 'bg-color-primario text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    Con impuestos
                  </button>
                </div>
              </div>

              {tm.modoPrecioTemporal === 'total' ? (
                <>
                  <input
                    type="number"
                    step="0.01"
                    value={tm.totalTemporalConImpuesto}
                    onChange={(e) => tm.actualizarTotalTemporal(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        tm.inputCantidadRef.current?.focus();
                      }
                    }}
                    placeholder="Total con ITBIS incluido"
                    className="w-full p-2 text-xl text-center font-semibold border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl"
                    tabIndex={1}
                  />
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    Costo unitario (base): {formatoMoneda(parseFloat(tm.precioTemporal) || 0)}
                  </p>
                </>
              ) : (
                <input
                  type="number"
                  step="0.01"
                  value={tm.precioTemporal}
                  onChange={(e) => tm.setPrecioTemporal(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      tm.inputCantidadRef.current?.focus();
                    }
                  }}
                  className="w-full p-2 text-xl text-center font-semibold border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl"
                  tabIndex={1}
                />
              )}

              <p className="text-xs text-gray-400 mt-1">
                Tasa ITBIS: {tm.obtenerTasaItbisEfectiva(tm.productoTemporal)}% (no editable)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cantidad{tm.productoTemporal.descripcion_unidad_medida && ` (${tm.productoTemporal.descripcion_unidad_medida})`}:
              </label>
              <input
                ref={tm.inputCantidadRef}
                type="number"
                value={tm.cantidadTemporal}
                onChange={(e) => {
                  const valor = e.target.value;
                  if (valor === '' || /^\d*\.?\d*$/.test(valor)) {
                    tm.setCantidadTemporal(valor);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    tm.agregarProductoAlTicket();
                  }
                }}
                min="0.01"
                step="0.01"
                className="w-full p-3 text-2xl text-center font-bold border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl"
                autoFocus
                tabIndex={2}
              />
            </div>

            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex justify-between items-center text-xs text-gray-500">
                <span>Antes de imp:</span>
                <span>
                  {formatoMoneda((parseFloat(tm.precioTemporal) || 0) * (parseFloat(tm.cantidadTemporal) || 0))}
                </span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-gray-600">Con imp:</span>
                <span className="text-2xl font-bold text-color-primario">
                  {formatoMoneda(
                    (parseFloat(tm.precioTemporal) || 0) * (parseFloat(tm.cantidadTemporal) || 0) *
                    (1 + tm.obtenerTasaItbisEfectiva(tm.productoTemporal) / 100)
                  )}
                </span>
              </div>
            </div>

            <button
              onClick={tm.agregarProductoAlTicket}
              className="w-full bg-color-secundario text-white py-3 rounded-xl font-semibold hover:bg-opacity-90 transition-all"
            >
              {tm.modoModal === 'editar' ? 'Actualizar' : 'Agregar a la Compra'}
            </button>
          </div>
        )}
      </PlantillaModal>

      {/* Modal de Pago */}
      <ModalPagoPOS
        mostrar={tm.mostrarModalPago}
        onClose={() => tm.setMostrarModalPago(false)}
        ticket={ticketActual}
        onConfirmarPago={procesarCompra}
        formatoMoneda={formatoMoneda}
        modo="pos"
      />

      {/* Modal para seleccionar proveedor */}
      <PlantillaModal 
        mostrar={tm.mostrarModalProveedores} 
        onClose={() => tm.setMostrarModalProveedores(false)}
        titulo="Seleccionar Proveedor"
      >
        <TablaEntidad
          tipoEntidad={2}
          descripcion="Seleccione un proveedor"
          setEntidadSeleccionada={tm.handleSeleccionarProveedor}
        />
      </PlantillaModal>

      {/* Modal de Búsqueda de Compras */}
      <PlantillaModal
        mostrar={tm.mostrarModalBusquedaCompras}
        onClose={() => tm.setMostrarModalBusquedaCompras(false)}
        titulo="Buscar Compras"
      >
        <div className="space-y-4 min-w-[600px]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-xl">
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Buscar (NCF, Proveedor o ID)</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-400 rounded-xl"
                placeholder="E41, nombre proveedor..."
                value={tm.filtrosBusqueda.busqueda}
                onChange={(e) => tm.setFiltrosBusqueda(prev => ({ ...prev, busqueda: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && tm.buscarCompras(tm.filtrosBusqueda)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Desde</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-400 rounded-xl"
                value={tm.filtrosBusqueda.fecha_desde}
                onChange={(e) => tm.setFiltrosBusqueda(prev => ({ ...prev, fecha_desde: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Hasta</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-400 rounded-xl"
                value={tm.filtrosBusqueda.fecha_hasta}
                onChange={(e) => tm.setFiltrosBusqueda(prev => ({ ...prev, fecha_hasta: e.target.value }))}
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => tm.buscarCompras(tm.filtrosBusqueda)}
                className="w-full px-4 py-2 bg-color-primario text-white rounded-xl hover:bg-opacity-90 transition"
              >
                Buscar
              </button>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={tm.limpiarFiltrosBusqueda}
              className="px-4 py-2 border border-gray-400 rounded-xl hover:bg-gray-100 transition"
            >
              Limpiar
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto border rounded-xl">
            {tm.cargandoCompras ? (
              <div className="text-center py-8"><SpinnerCargando /></div>
            ) : tm.comprasBuscadas.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No se encontraron compras
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-color-primario text-white sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase">ID</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase">Proveedor</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase">Fecha</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase">Estado DGII</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase">Total</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase">Balance</th>
                    <th className="px-4 py-2 text-center text-xs font-medium uppercase">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {tm.comprasBuscadas.map((compra) => (
                    <tr key={compra.id_compra} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono text-sm">{compra.id_compra}</td>
                      <td className="px-4 py-2 text-sm truncate max-w-[200px]">
                        {compra.nombre_proveedor || 'Sin proveedor'}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {new Date(compra.fecha_hora).toLocaleDateString('es-DO')}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {compra.dgii_estado ? (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            compra.dgii_estado === 'PendienteEnvio'
                              ? 'bg-amber-100 text-amber-800'
                              : compra.dgii_estado === 'EnProceso'
                                ? 'bg-blue-100 text-blue-800'
                                : compra.dgii_estado === 'Aceptado'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : compra.dgii_estado === 'Rechazado'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-700'
                          }`}>
                            {compra.dgii_estado}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right font-medium">
                        {formatoMoneda(compra.monto_total)}
                      </td>
                      <td className="px-4 py-2 text-right font-medium">
                        <span className={parseFloat(compra.balance) > 0 ? 'text-red-600' : 'text-green-600'}>
                          {formatoMoneda(compra.balance)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex justify-center gap-2">
                          {/* Botón Editar — solo compras sin NCF E41 */}
                          {!(compra.ncf && compra.ncf.startsWith('E41')) && (
                            <button
                              onClick={() => cargarCompraParaEditar(compra)}
                              className="px-3 py-1 bg-amber-500 text-white text-sm rounded-lg hover:bg-amber-600 transition"
                              title="Editar compra"
                            >
                              <FaEdit size={14} />
                            </button>
                          )}
                          {compra.es_informal && compra.ncf && (
                            <button
                              onClick={() => {
                                tm.setIdCompraPDF(compra.id_compra);
                                tm.setPdfBase64(null);
                                tm.setMostrarPDFCompra(true);
                              }}
                              className="px-3 py-1 bg-color-secundario text-white text-sm rounded-lg hover:bg-opacity-90 transition"
                              title="Ver PDF"
                            >
                              <FaFileAlt size={14} />
                            </button>
                          )}
                          {compra.es_electronica && ['PendienteEnvio', 'Contingencia'].includes(compra.dgii_estado) && (
                            <button
                              onClick={() => reintentarEnvioCompraDGII(compra.id_compra)}
                              disabled={idCompraReintentando === compra.id_compra}
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition disabled:opacity-60"
                              title="Reintentar envío DGII"
                            >
                              <FaSync className={idCompraReintentando === compra.id_compra ? 'animate-spin' : ''} size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </PlantillaModal>

      {/* Modal Confirmación Serie B (Contingencia) */}
      <PlantillaModal
        mostrar={mostrarConfirmacionSerieB}
        onClose={() => {
          setMostrarConfirmacionSerieB(false);
          setPayloadPendiente(null);
        }}
        titulo="⚠️ Modo Contingencia"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-yellow-50 rounded-xl border border-yellow-200">
            <FaExclamationTriangle className="text-yellow-600 text-xl flex-shrink-0 mt-1" />
            <div>
              <p className="font-semibold text-yellow-800">Serie E agotada o no disponible</p>
              <p className="text-sm text-yellow-700 mt-1">{mensajeContingencia}</p>
            </div>
          </div>
          
          <p className="text-gray-600">
            ¿Desea crear la compra con Serie B (física/contingencia)?
          </p>
          
          <div className="flex gap-3 justify-end pt-4">
            <button
              onClick={() => {
                setMostrarConfirmacionSerieB(false);
                setPayloadPendiente(null);
              }}
              className="px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={confirmarUsarSerieB}
              disabled={tm.procesandoCobro}
              className="px-4 py-2 bg-yellow-600 text-white rounded-xl hover:bg-yellow-700 transition disabled:opacity-50"
            >
              {tm.procesandoCobro ? 'Procesando...' : 'Usar Serie B'}
            </button>
          </div>
        </div>
      </PlantillaModal>

      {/* Modal PDF Compra */}
      <ModalPDFBase64
        mostrar={tm.mostrarPDFCompra}
        onClose={() => {
          tm.setMostrarPDFCompra(false);
          tm.setPdfBase64(null);
          tm.setIdCompraPDF(null);
        }}
        idCompra={tm.idCompraPDF || tm.compraCreada?.id_compra}
        pdfBase64Directo={tm.pdfBase64}
        ncf={tm.compraCreada?.ncf}
        tituloDocumento="Compra"
        tipoDocumento="compra"
      />

      {/* Modal Resumen de Ajustes de Inventario y Pagos */}
      <PlantillaModal
        mostrar={mostrarResumenInventario}
        onClose={() => {
          // Solo permitir cerrar si no hay pagos anulados o si el checkbox está marcado
          if (pagosAnulados === 0 || confirmacionPagosAnulados) {
            setMostrarResumenInventario(false);
            setResumenInventario(null);
            setPagosAnulados(0);
            setConfirmacionPagosAnulados(false);
          }
        }}
        titulo="Resumen de Cambios"
      >
        <div className="space-y-4 min-w-[600px]">
          {/* Fallback: no hubo cambios de inventario ni pagos anulados */}
          {(!resumenInventario || resumenInventario.length === 0) && pagosAnulados === 0 && (
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl border border-green-200">
              <FiCheckCircle className="text-green-600 text-xl flex-shrink-0" />
              <p className="text-green-800 font-medium">
                Compra actualizada correctamente. No se requirieron ajustes de inventario ni anulación de pagos.
              </p>
            </div>
          )}

          {/* Alerta de Pagos Anulados */}
          {pagosAnulados > 0 && (
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl">
              <div className="flex items-start gap-3">
                <FaExclamationTriangle className="text-amber-600 text-xl flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-bold text-amber-900 mb-1">Pagos Anulados Automáticamente</h4>
                  <p className="text-sm text-amber-800 leading-relaxed">
                    Se anularon <span className="font-bold">{pagosAnulados}</span> pago(s) existente(s) debido al cambio en el total de la compra.
                  </p>
                  <p className="text-sm text-amber-700 mt-2 font-medium">
                    ⚠️ Debe aplicar los pagos nuevamente desde <span className="font-bold">Cuentas por Pagar</span>.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tabla de Ajustes de Inventario */}
          {resumenInventario && resumenInventario.length > 0 && (
            <>
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl border border-green-200">
                <FiCheckCircle className="text-green-600 text-xl flex-shrink-0" />
                <p className="text-green-800 font-medium">
                  Compra actualizada. Se realizaron los siguientes ajustes de inventario:
                </p>
              </div>

              <div className="overflow-x-auto border rounded-xl">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Producto</th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase">Ant.</th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase">Nueva</th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase">Cambio</th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase">Acción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {resumenInventario.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm font-medium">{item.descripcion}</td>
                        <td className="px-4 py-2 text-center text-sm text-gray-500">{item.cantidad_anterior}</td>
                        <td className="px-4 py-2 text-center text-sm font-semibold">{item.cantidad_nueva}</td>
                        <td className="px-4 py-2 text-center text-sm font-bold">
                          <span className={item.delta > 0 ? 'text-green-600' : 'text-red-600'}>
                            {item.delta > 0 ? '+' : ''}{item.delta}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            item.accion === 'aumentado'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {item.accion === 'aumentado' ? '↑ Aumentado' : '↓ Reducido'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Checkbox de confirmación (obligatorio si hay pagos anulados) */}
          {pagosAnulados > 0 && (
            <div className="bg-gray-50 p-4 rounded-xl border-2 border-gray-200">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmacionPagosAnulados}
                  onChange={(e) => setConfirmacionPagosAnulados(e.target.checked)}
                  className="mt-1 h-5 w-5 text-color-primario rounded focus:ring-2 focus:ring-color-primario"
                />
                <span className="text-sm text-gray-700 font-medium leading-relaxed">
                  Entiendo que los pagos fueron anulados y que debo aplicarlos nuevamente desde Cuentas por Pagar.
                </span>
              </label>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <button
              onClick={() => {
                setMostrarResumenInventario(false);
                setResumenInventario(null);
                setPagosAnulados(0);
                setConfirmacionPagosAnulados(false);
                toast.success('Compra actualizada correctamente');
                // Now clean ticket
                tm.limpiarTicketActivo();
                tm.setMostrarModalPago(false);
              }}
              disabled={pagosAnulados > 0 && !confirmacionPagosAnulados}
              className={`px-6 py-2 rounded-xl font-medium transition ${
                pagosAnulados > 0 && !confirmacionPagosAnulados
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-color-primario text-white hover:bg-opacity-90'
              }`}
            >
              Entendido
            </button>
          </div>
        </div>
      </PlantillaModal>

      {/* Dock de acciones */}
      <DockVerticalDerecha
        handleBotonCrear={tm.crearNuevoTicket}
        handleBotonBuscar={tm.handleBotonBuscar}
      />
    </div>
  );
}

